import { useEffect, useRef } from 'react';
import useExitIntent from '@/app/hooks/useExitIntent';
import { leaveTriggersFor, watchInactivity } from '@/app/lib/guideChat/triggers';
import type { FormStep, OpenerTrigger } from '@/app/lib/guideChat/types';
import { currentFormStep, deviceClass, fieldLabel, isRiskyField } from './pageContext';

const FIELD_IDLE_MS = 18_000;
const RISKY_FIELD_IDLE_MS = 8_000;
const RISKY_STEPS: FormStep[] = ['borrower', 'health', 'bank'];
const FIELD_SELECTOR = 'input, select, textarea, [role="combobox"], button[type="button"].w-full';

interface Options {
  enabled: boolean;
  onTrigger: (trigger: OpenerTrigger, step: FormStep | null) => void;
}

// §4.3: friction signals (idle field, hesitation) and leaving signals (focus loss and exit intent on desktop, inactivity on mobile), one opener per wizard step.
export function useFormTriggers({ enabled, onTrigger }: Options) {
  const onTriggerRef = useRef(onTrigger);
  const fireRef = useRef<((trigger: OpenerTrigger) => void) | null>(null);

  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled) return;
    let step = currentFormStep();
    let firedStep: FormStep | null | undefined;
    let idleTimer: number | null = null;
    let backClicks = 0;
    let hiddenSince: number | null = null;
    const errorCounts = new Map<string, number>();
    const inError = new WeakSet<Element>();

    const leaving = leaveTriggersFor('form', deviceClass());
    const fire = (trigger: OpenerTrigger) => {
      if (firedStep === step) return;
      firedStep = step;
      onTriggerRef.current(trigger, step);
    };
    fireRef.current = fire;
    const clearIdle = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = null;
    };
    const armIdle = (el: Element) => {
      clearIdle();
      const risky = RISKY_STEPS.includes(step as FormStep) && isRiskyField(fieldLabel(el));
      idleTimer = window.setTimeout(() => fire(risky ? 'risky_step' : 'field_idle'), risky ? RISKY_FIELD_IDLE_MS : FIELD_IDLE_MS);
    };
    const fieldOf = (target: EventTarget | null): Element | null => {
      const el = target as Element | null;
      return el?.closest?.('[data-loan-screen]') ? el.closest(FIELD_SELECTOR) : null;
    };

    const onFocusIn = (e: FocusEvent) => {
      const el = fieldOf(e.target);
      if (el) armIdle(el);
    };
    const onFocusOut = () => clearIdle();
    const onActivity = (e: Event) => {
      const el = fieldOf(e.target);
      if (el && idleTimer) armIdle(el);
      if ((e.target as Element | null)?.closest?.('[data-test="previous-button"]') && ++backClicks >= 2) fire('hesitation');
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hiddenSince = Date.now();
      else if (hiddenSince) {
        hiddenSince = null;
        fire('focus_loss');
      }
    };
    const onBlur = () => {
      hiddenSince = Date.now();
    };
    const onFocus = () => {
      if (hiddenSince) {
        hiddenSince = null;
        fire('focus_loss');
      }
    };

    const observer = new MutationObserver(() => {
      const nextStep = currentFormStep();
      if (nextStep !== step) {
        step = nextStep;
        backClicks = 0;
        errorCounts.clear();
        clearIdle();
      }
      document.querySelectorAll('[data-loan-screen] .loan-field-has-error').forEach((el) => {
        if (inError.has(el)) return;
        inError.add(el);
        const key = fieldLabel(el) ?? 'unknown';
        const count = (errorCounts.get(key) ?? 0) + 1;
        errorCounts.set(key, count);
        if (count >= 2) fire('hesitation');
      });
      document.querySelectorAll('[data-loan-screen] .loan-form-field > div').forEach((el) => {
        if (inError.has(el) && !el.classList.contains('loan-field-has-error')) inError.delete(el);
      });
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('input', onActivity);
    document.addEventListener('change', onActivity);
    document.addEventListener('keydown', onActivity);
    document.addEventListener('click', onActivity);
    if (leaving.includes('focus_loss')) {
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('blur', onBlur);
      window.addEventListener('focus', onFocus);
    }
    const stopInactivity = leaving.includes('inactivity') ? watchInactivity(window, () => fire('inactivity')) : null;

    return () => {
      fireRef.current = null;
      stopInactivity?.();
      observer.disconnect();
      clearIdle();
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('input', onActivity);
      document.removeEventListener('change', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('click', onActivity);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);

  useExitIntent(() => fireRef.current?.('exit_intent'), enabled && leaveTriggersFor('form', deviceClass()).includes('exit_intent'));
}
